import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Send, FileDown, CheckCheck, FileText, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StatusBadge } from "@/components/StatusBadge";
import { api, download, errMsg, fmtDate, openBlob, todayIso } from "@/lib/api";

const TABS = [["", "Tutte"], ["confermata", "Da inviare"], ["validata", "Validate"], ["errore", "Errori"], ["inviata", "Inviate"], ["ricevuta", "Ricevute"], ["bozza", "Bozze"]];
const SENDABLE = ["confermata", "validata", "errore"];

function Receipts() {
  const [list, setList] = useState([]);
  const [day, setDay] = useState(new Date(Date.now() - 864e5).toLocaleDateString("sv-SE"));
  const [month, setMonth] = useState(todayIso().slice(0, 7));
  const load = () => api.get("/ricevute").then(({ data }) => setList(data));
  useEffect(() => {
    load();
  }, []);
  const fetchR = async () => {
    try {
      const { data } = await api.post("/ricevute/fetch", { date: day });
      toast.success(`Ricevuta del ${fmtDate(day)} archiviata (${data.guests} schedine)`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const shown = list.filter((r) => r.date.startsWith(month));
  return (
    <div className="rounded-2xl border bg-white p-5 space-y-4" data-testid="receipts-section">
      <h2 className="font-display text-xl font-bold">Archivio ricevute PDF</h2>
      <div className="flex flex-wrap gap-2 items-center">
        <Input type="date" className="w-auto" value={day} max={new Date(Date.now() - 864e5).toLocaleDateString("sv-SE")} onChange={(e) => setDay(e.target.value)} data-testid="receipt-date-input" />
        <Button variant="outline" onClick={fetchR} data-testid="fetch-receipt-button"><FileDown className="h-4 w-4 mr-2" />Scarica ricevuta del giorno</Button>
        <span className="text-stone-400 mx-2 hidden sm:inline">|</span>
        <Input type="month" className="w-auto" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="receipt-month-filter" />
      </div>
      {shown.length === 0 ? <p className="text-sm text-stone-500">Nessuna ricevuta nel mese selezionato.</p> : (
        <ul className="divide-y">
          {shown.map((r) => (
            <li key={r.id} className="flex items-center justify-between py-2">
              <span className="text-sm"><b>{fmtDate(r.date)}</b> · {r.guests} schedine</span>
              <Button size="sm" variant="ghost" onClick={() => openBlob(`/ricevute/${r.id}/pdf`).catch((e) => toast.error(errMsg(e)))} data-testid={`open-receipt-${r.date}`}><FileText className="h-4 w-4 mr-1" />PDF</Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function Schedine() {
  const [params, setParams] = useSearchParams();
  const status = params.get("status") || "";
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get("/schedine", { params: { status } }).then(({ data }) => {
      setRows(data);
      setSel([]);
    }).catch((e) => toast.error(errMsg(e)));
  }, [status]);
  useEffect(() => {
    load();
  }, [load]);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const sendable = rows.filter((r) => SENDABLE.includes(r.status));
  const run = async (op) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/schedine/${op}`, { guest_ids: sel });
      if (op === "mark-sent") toast.success("Schedine segnate come inviate");
      else if (data.errors) toast.error(`${data.ok} ok, ${data.errors} con errori: controlla le righe in rosso`);
      else toast.success(op === "validate" ? `${data.ok} schedine validate` : `${data.ok} schedine inviate alla Polizia di Stato`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  const txt = () => download("/schedine/file", { ids: sel.join(",") }).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="space-y-6" data-testid="schedine-page">
      <div>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">Schedine Alloggiati</h1>
        <p className="text-stone-500 text-sm mt-1">Bozza → confermata → validata → inviata → ricevuta archiviata. Invio entro 24 ore dall'arrivo.</p>
      </div>
      <Tabs value={status} onValueChange={(v) => setParams(v ? { status: v } : {})}>
        <TabsList className="flex-wrap h-auto">{TABS.map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`schedine-tab-${v || "all"}`}>{l}</TabsTrigger>)}</TabsList>
      </Tabs>
      <div className="flex flex-wrap gap-2 items-center rounded-xl bg-stone-100 p-3 sticky top-14 lg:top-0 z-20">
        <label className="flex items-center gap-2 text-sm mr-2">
          <Checkbox checked={sendable.length > 0 && sel.length === sendable.length} onCheckedChange={(v) => setSel(v ? sendable.map((r) => r.id) : [])} data-testid="select-all-schedine" />
          Seleziona inviabili ({sendable.length})
        </label>
        <Button size="sm" variant="outline" disabled={!sel.length || busy} onClick={() => run("validate")} data-testid="validate-schedine-button"><ShieldCheck className="h-4 w-4 mr-1" />Valida</Button>
        <Button size="sm" disabled={!sel.length || busy} onClick={() => run("send")} data-testid="send-schedine-button"><Send className="h-4 w-4 mr-1" />Invia</Button>
        <Button size="sm" variant="outline" disabled={!sel.length} onClick={txt} data-testid="download-txt-button"><FileDown className="h-4 w-4 mr-1" />File .txt</Button>
        <Button size="sm" variant="ghost" disabled={!sel.length || busy} onClick={() => run("mark-sent")} data-testid="mark-sent-button"><CheckCheck className="h-4 w-4 mr-1" />Caricate a mano sul portale</Button>
      </div>
      <div className="grid gap-2" data-testid="schedine-list">
        {rows.length === 0 && <p className="text-sm text-stone-500">Nessuna schedina in questa vista.</p>}
        {rows.map((r) => (
          <div key={r.id} className={`rounded-xl border bg-white p-4 ${r.status === "errore" ? "border-rose-300" : ""}`} data-testid={`schedina-row-${r.id}`}>
            <div className="flex flex-wrap items-center gap-3">
              {SENDABLE.includes(r.status) && <Checkbox checked={sel.includes(r.id)} onCheckedChange={() => toggle(r.id)} data-testid={`schedina-checkbox-${r.id}`} />}
              <div className="flex-1 min-w-[180px]">
                <p className="font-semibold">{r.cognome} {r.nome} <span className="text-xs font-normal text-stone-500">· {r.tipo_label}</span></p>
                <p className="text-xs text-stone-500">{r.room_name} · arrivo {fmtDate(r.arrival)} → {fmtDate(r.departure)}{r.sent_date ? ` · inviata il ${fmtDate(r.sent_date)}` : ""}</p>
              </div>
              {r.late && <span className="flex items-center gap-1 text-xs font-semibold text-rose-700" data-testid={`schedina-late-${r.id}`}><Clock className="h-3 w-3" />Oltre 24h</span>}
              <StatusBadge status={r.status} testId={`schedina-status-${r.id}`} />
              <Link to={`/prenotazioni/${r.booking_id}`} className="text-sm text-primary hover:underline" data-testid={`schedina-edit-${r.id}`}>{["inviata", "ricevuta"].includes(r.status) ? "Dettagli" : "Correggi"}</Link>
            </div>
            {r.error && <p className="mt-2 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-800" data-testid={`schedina-error-${r.id}`}>{r.error}</p>}
          </div>
        ))}
      </div>
      <Receipts />
    </div>
  );
}
